import React, { useState, useEffect, useMemo } from "react";
import { makeStyles, Tooltip } from "@material-ui/core";
import {
  capitalize,
  compare,
  exportToXlsx,
  getDateValue,
  secondsToTime
} from "../../../utils/helpers";
import DateFnsUtils from "@date-io/date-fns";
import CommonMaterialTable from "../commonTable/CommonMaterialTable";
import ProjectsEditDialog from "../projectsDialog/ProjectsEditDialog";
import API from "../../../utils/axios";
import { connect } from "react-redux";
import { getAppProjects } from "../../../store/actions/account";
import { setAppStops } from "../../../store/actions/account";
import { APP_STOPS_HEADER_ROW } from "../../data/constants";

const useStyles = makeStyles(theme => ({
  customTableWrapper: {
    "& .MuiTable-root": {
      "& td:first-child, th:first-child": {
        width: "34px !important"
      }
    }
  },
  tooltip: {
    backgroundColor: "#fff",
    color: theme.palette.text.primary,
    fontSize: "0.8125rem",
    lineHeight: "0.9375rem",
    fontWeight: 400,
    boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.24)",
    padding: theme.spacing(1.5),
    maxWidth: "360px"
  }
}));

const dateFns = new DateFnsUtils();

function SingleApp({
  app,
  appInfo,
  handleClose,
  open,
  appStops,
  saveDisabled,
  setSaveDisabled,
  setAppStops,
  getAppProjects
}) {
  const classes = useStyles();

  const { data, total_quantity: totalQuantity } = appStops;

  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    id: null
  });

  const [loading, setLoading] = useState(false);
  const [loadingCircle, setLoadingCircle] = useState(false);
  const [selected, setSelected] = useState([]);
  const [currentAction, setCurrentAction] = useState("overview");
  const [currentPage, setCurrentPage] = useState(0);
  // Did this to prevent rentman's bug which sends totalQuantity as 0.
  const [pagesLimit, setPagesLimit] = useState(10000);
  const appName = appInfo?.name;
  const appId = appInfo?.id;

  const getStopsPerPage = () => {
    setLoading(true);
    if (appInfo) {
      API.get(`v1/apps/stops?uuids=${appInfo.id}&page=${currentPage}`)
        .then(res => {
          if (res.data.length === 0) {
            setCurrentPage(page => page - 1);
            setPagesLimit(currentPage);
          }
          setAppStops(res.data);
        })
        .catch(error => console.log(error))
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    getStopsPerPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const handleMultipleDownload = () => {
    exportToXlsx("selected_stops.xlsx", [
      APP_STOPS_HEADER_ROW,
      ...selected.map(item => [
        item.status,
        item.stop.due_date
          ? dateFns.format(
              new Date(getDateValue(item.stop.due_date)),
              "dd/MM/yyyy"
            )
          : "",
        item.stop.project_id,
        item.stop.type,
        item.stop.information.recipient_name,
        item.stop.information.street,
        item.stop.information.city,
        item.stop.information.zip_code,
        item.stop.information.country,
        item.stop.address.lat,
        item.stop.address.lng,
        item.stop.duration,
        secondsToTime(item.stop.earliest_time),
        secondsToTime(item.stop.latest_time),
        item.stop.information.email,
        item.stop.information.recipient_phone,
        item.stop.priority ? "Yes" : "No",
        item.stop.signature ? "Yes" : "No",
        item.stop.age ? "Yes" : "No",
        item.stop.photo ? "Yes" : "No",
        item.stop.delivery_note,
        item.stop.quantity,
        item.stop.weight,
        item.stop.volume,
        item.stop.tracking.id
      ])
    ]);
  };

  const handleOpen = () => {
    setCurrentAction("add");
  };

  useEffect(() => {
    if (currentAction === "overview") getAppProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAction]);

  const modifyAppStops = () => {
    return data.map((item, i) => {
      return {
        id: i + 1,
        status: item.status,
        stop: item.stop
      };
    });
  };

  const commonMaterialTable = useMemo(() => {
    return (
      <CommonMaterialTable
        isApp
        confirmDialogState={confirmDialogState}
        setConfirmDialogState={setConfirmDialogState}
        loadingCircle={loadingCircle}
        setLoadingCircle={setLoadingCircle}
        handleClose={handleClose}
        selected={selected}
        data={loading ? [] : modifyAppStops()}
        title={app}
        singular="stop"
        plural="stops"
        downloadable
        pagination={{
          enabled: true,
          currentPage,
          setCurrentPage,
          isNextButtonDisabled: pagesLimit - 1 === currentPage,
          ...(app !== "Rentman" && {
            totalQuantity
          })
        }}
        getDataPerPage={getStopsPerPage}
        handleMultipleDownload={handleMultipleDownload}
        customWrapperClass={classes.customTableWrapper}
        handleDelete={null}
        handleMultipleDelete={null}
        setSelected={setSelected}
        loading={loading}
        handleAdd={null}
        handleEdit={null}
        handleOpen={handleOpen}
        open={open}
        columns={[
          {
            title: "Status",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "115px"
            },
            headerStyle: {
              width: "115px"
            },
            customFilterAndSearch: (val, rowData) => {
              const status = rowData.status;
              return (status || "").toLowerCase().includes(val.toLowerCase());
            },
            customSort: (a, b) => {
              return compare(a.status, b.status);
            },
            render: rowData => {
              return <span>{rowData.status}</span>;
            }
          },
          // {
          //   title: "Date created",
          //   field: "date",
          //   editable: "never",
          //   cellStyle: {
          //     width: "117px"
          //   },
          //   headerStyle: {
          //     width: "117px"
          //   },
          //   searchable: false,
          //   customSort: (a, b) => {
          //     // date sort
          //     const dateA = a.stop.created_at;
          //     const dateB = b.stop.created_at;
          //     if (+new Date(dateA) !== +new Date(dateB)) {
          //       return new Date(dateA) - new Date(dateB);
          //     } else {
          //       return (
          //         new Date(+a.stop.created_at) - new Date(+b.stop.created_at)
          //       );
          //     }
          //   },
          //   render: rowData => {
          //     return (
          //       <span>
          //         {rowData.stop.created_at
          //           ? dateFns.format(
          //               new Date(getDateValue(rowData.stop.created_at)),
          //               "dd/MM/yyyy"
          //             )
          //           : ""}
          //       </span>
          //     );
          //   }
          // },
          ...(appInfo?.name === "rentman"
            ? [
                {
                  title: "Due date",
                  field: "due_date",
                  editable: "never",

                  cellStyle: {
                    width: "120px"
                  },
                  headerStyle: {
                    width: "120px"
                  },
                  customSort: (a, b) => {
                    const dateA = a.stop.due_date;
                    const dateB = b.stop.due_date;
                    if (+new Date(dateA) !== +new Date(dateB)) {
                      return new Date(dateA) - new Date(dateB);
                    } else {
                      return (
                        new Date(+a.stop.due_date) - new Date(+b.stop.due_date)
                      );
                    }
                  },
                  customFilterAndSearch: (val, rowData) => {
                    const date = rowData.stop.due_date
                      ? dateFns.format(
                          new Date(getDateValue(rowData.stop.due_date)),
                          "dd MMM yyyy"
                        )
                      : "";
                    return date.toLowerCase().includes(val.toLowerCase());
                  },
                  render: rowData => {
                    return (
                      <span>
                        {rowData.stop.due_date
                          ? dateFns.format(
                              new Date(getDateValue(rowData.stop.due_date)),
                              "dd MMM yyyy"
                            )
                          : ""}
                      </span>
                    );
                  }
                },
                {
                  title: "Project number",
                  field: "project_id",
                  editable: "never",

                  cellStyle: {
                    width: "120px"
                  },
                  headerStyle: {
                    width: "120px"
                  },
                  customFilterAndSearch: (val, rowData) => {
                    const project_id = rowData.stop.project_id;
                    return (project_id || "")
                      .toLowerCase()
                      .includes(val.toLowerCase());
                  },
                  customSort: (a, b) => {
                    return compare(a.stop.project_id, b.stop.project_id);
                  },
                  render: rowData => {
                    return <span>{capitalize(rowData.stop.project_id)}</span>;
                  }
                }
              ]
            : []),
          {
            title: "Type",
            field: "type",
            editable: "never",

            cellStyle: {
              width: "77px"
            },
            headerStyle: {
              width: "77px"
            },
            customFilterAndSearch: (val, rowData) => {
              const type = rowData.stop.type;
              return (type || "").toLowerCase().includes(val.toLowerCase());
            },
            customSort: (a, b) => {
              return compare(a.stop.type, b.stop.type);
            },
            render: rowData => {
              return <span>{capitalize(rowData.stop.type)}</span>;
            }
          },
          {
            title: "Recipient Name",
            field: "information.name",
            editable: "never",

            cellStyle: {
              width: "164px"
            },
            headerStyle: {
              width: "164px"
            },
            customFilterAndSearch: (val, rowData) => {
              const name = rowData.stop.information.recipient_name;
              return (name || "").toLowerCase().includes(val.toLowerCase());
            },
            customSort: (a, b) => {
              return compare(
                a.stop.information.recipient_name,
                b.stop.information.recipient_name
              );
            },
            render: rowData => {
              return (
                <Tooltip
                  title={rowData.stop.information.recipient_name}
                  placement="bottom-start"
                  classes={{ tooltip: classes.tooltip }}
                  enterDelay={1000}
                >
                  <span>{rowData.stop.information.recipient_name}</span>
                </Tooltip>
              );
            }
          },
          {
            title: "Street",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "130px"
            },
            headerStyle: {
              width: "130px"
            },
            customFilterAndSearch: (val, rowData) => {
              const street = rowData.stop.information.street;
              return (street || "").toLowerCase().includes(val.toLowerCase());
            },
            customSort: (a, b) => {
              return compare(
                a.stop.information.street,
                b.stop.information.street
              );
            },
            render: rowData => {
              return (
                <Tooltip
                  title={rowData.stop.information.street}
                  placement="bottom-start"
                  classes={{ tooltip: classes.tooltip }}
                  enterDelay={1000}
                >
                  <span>{rowData.stop.information.street}</span>
                </Tooltip>
              );
            }
          },
          {
            title: "City",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "102px"
            },
            headerStyle: {
              width: "102px"
            },
            customFilterAndSearch: (val, rowData) => {
              const city = rowData.stop.information.city;
              return (city || "").toLowerCase().includes(val.toLowerCase());
            },
            customSort: (a, b) => {
              return compare(a.stop.information.city, b.stop.information.city);
            },
            render: rowData => {
              return (
                <Tooltip
                  title={rowData.stop.information.city}
                  placement="bottom-start"
                  classes={{ tooltip: classes.tooltip }}
                  enterDelay={1000}
                >
                  <span>{rowData.stop.information.city}</span>
                </Tooltip>
              );
            }
          },
          {
            title: "Postal Code",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "108px"
            },
            headerStyle: {
              width: "108px"
            },
            customFilterAndSearch: (val, rowData) => {
              const zipCode = rowData.stop.information.zip_code;
              return (zipCode || "").toLowerCase().includes(val.toLowerCase());
            },
            customSort: (a, b) => {
              return compare(
                a.stop.information.zip_code,
                b.stop.information.zip_code
              );
            },
            render: rowData => {
              return <span>{rowData.stop.information.zip_code}</span>;
            }
          },
          {
            title: "Country",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "87px"
            },
            headerStyle: {
              width: "87px"
            },
            customFilterAndSearch: (val, rowData) => {
              const country = rowData.stop.information.country;
              return (country || "").toLowerCase().includes(val.toLowerCase());
            },
            customSort: (a, b) => {
              return compare(
                a.stop.information.country,
                b.stop.information.country
              );
            },
            render: rowData => {
              return (
                <Tooltip
                  title={rowData.stop.information.country}
                  placement="bottom-start"
                  classes={{ tooltip: classes.tooltip }}
                  enterDelay={1000}
                >
                  <span>{rowData.stop.information.country}</span>
                </Tooltip>
              );
            }
          },
          {
            title: "Latitude",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "98px"
            },
            headerStyle: {
              width: "98px"
            },
            searchable: false,
            customSort: (a, b) => a.stop.address.lat - b.stop.address.lat,
            render: rowData => {
              return <span>{rowData.stop.address.lat}</span>;
            }
          },
          {
            title: "Longitude",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "98px"
            },
            headerStyle: {
              width: "98px"
            },
            searchable: false,
            customSort: (a, b) => a.stop.address.lng - b.stop.address.lng,
            render: rowData => {
              return <span>{rowData.stop.address.lng}</span>;
            }
          },
          {
            title: "Duration",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "90px"
            },
            headerStyle: {
              width: "90px"
            },
            searchable: false,
            customSort: (a, b) => a.stop.duration - b.stop.duration,
            render: rowData => {
              return (
                <span>
                  {rowData.stop.duration && rowData.stop.duration + "m"}
                </span>
              );
            }
          },
          {
            title: "Earliest Time",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "115px"
            },
            headerStyle: {
              width: "115px"
            },
            searchable: false,
            customSort: (a, b) => a.stop.earliest_time - b.stop.earliest_time,
            render: rowData => {
              return <span>{secondsToTime(rowData.stop.earliest_time)}</span>;
            }
          },
          {
            title: "Latest Time",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "108px"
            },
            headerStyle: {
              width: "108px"
            },
            searchable: false,
            customSort: (a, b) => a.stop.latest_time - b.stop.latest_time,
            render: rowData => {
              return <span>{secondsToTime(rowData.stop.latest_time)}</span>;
            }
          },
          {
            title: "Email",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "142px"
            },
            headerStyle: {
              width: "142px"
            },
            customFilterAndSearch: (val, rowData) => {
              const email = rowData.stop.information.email;
              return (email || "").toLowerCase().includes(val.toLowerCase());
            },
            customSort: (a, b) => {
              return compare(
                a.stop.information.email,
                b.stop.information.email
              );
            },
            render: rowData => {
              return (
                <Tooltip
                  title={rowData.stop.information.email}
                  placement="bottom-start"
                  classes={{ tooltip: classes.tooltip }}
                  enterDelay={1000}
                >
                  <span>{rowData.stop.information.email}</span>
                </Tooltip>
              );
            }
          },
          {
            title: "Phone",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "126px"
            },
            headerStyle: {
              width: "126px"
            },
            customFilterAndSearch: (val, rowData) => {
              const phone = rowData.stop.information.recipient_phone;
              return (phone || "").toLowerCase().includes(val.toLowerCase());
            },
            customSort: (a, b) =>
              +a.stop.information.recipient_phone -
              +b.stop.information.recipient_phone,
            render: rowData => {
              return <span>{rowData.stop.information.recipient_phone}</span>;
            }
          },
          {
            title: "Priority",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "82px"
            },
            headerStyle: {
              width: "82px"
            },
            searchable: false,
            customSort: (a, b) => a.stop.priority - b.stop.priority,
            render: rowData => {
              return <span>{rowData.stop.priority ? "Yes" : "No"}</span>;
            }
          },
          {
            title: "Signature",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "96px"
            },
            headerStyle: {
              width: "96px"
            },
            searchable: false,
            customSort: (a, b) => {
              const signatureA = a.stop.signature ? "Yes" : "No";
              const signatureB = b.stop.signature ? "Yes" : "No";
              return compare(signatureA, signatureB);
            },
            render: rowData => {
              return <span>{rowData.stop.signature ? "Yes" : "No"}</span>;
            }
          },
          {
            title: "Age",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "66px"
            },
            headerStyle: {
              width: "66px"
            },
            searchable: false,
            customSort: (a, b) => {
              const ageA = a.stop.age ? "Yes" : "No";
              const ageB = b.stop.age ? "Yes" : "No";
              return compare(ageA, ageB);
            },
            render: rowData => {
              return <span>{rowData.stop.age ? "Yes" : "No"}</span>;
            }
          },
          {
            title: "Photo",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "76px"
            },
            headerStyle: {
              width: "76px"
            },
            searchable: false,
            customSort: (a, b) => {
              const pictureA = a.stop.photo ? "Yes" : "No";
              const pictureB = b.stop.photo ? "Yes" : "No";
              return compare(pictureA, pictureB);
            },
            render: rowData => {
              return <span>{rowData.stop.photo ? "Yes" : "No"}</span>;
            }
          },
          {
            title: "Delivery Note",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "116px"
            },
            headerStyle: {
              width: "116px"
            },
            customFilterAndSearch: (val, rowData) => {
              const notes = rowData.stop.delivery_note;
              return (notes || "").toLowerCase().includes(val.toLowerCase());
            },
            customSort: (a, b) => {
              return compare(a.stop.delivery_note, b.stop.delivery_note);
            },
            render: rowData => {
              return (
                <Tooltip
                  title={rowData.stop.delivery_note || ""}
                  placement="bottom-start"
                  classes={{ tooltip: classes.tooltip }}
                  enterDelay={1000}
                >
                  <span>{rowData.stop.delivery_note || ""}</span>
                </Tooltip>
              );
            }
          },
          // TODO: do not need to show tags columns yet
          // {
          //   title: "Vehicle Tags",
          //   field: "date",
          //   editable: "never",

          //   cellStyle: {
          //     width: "112px"
          //   },
          //   headerStyle: {
          //     width: "112px"
          //   },
          //   searchable: false,
          //   render: rowData => {
          //     const tags = rowData.stop.tags
          //       ? rowData.stop.tags.filter(tag => tag.type === "VEHICLE")
          //       : [];
          //     return (
          //       <span>
          //         {tags.length
          //           ? tags.length === 1
          //             ? "1 tag"
          //             : tags.length + " tags"
          //           : ""}
          //       </span>
          //     );
          //   }
          // },
          // {
          //   title: "Driver Tags",
          //   field: "date",
          //   editable: "never",

          //   cellStyle: {
          //     width: "105px"
          //   },
          //   headerStyle: {
          //     width: "105px"
          //   },
          //   searchable: false,
          //   render: rowData => {
          //     const tags = rowData.stop.tags
          //       ? rowData.stop.tags.filter(tag => tag.type === "DRIVER")
          //       : [];
          //     return (
          //       <span>
          //         {tags.length
          //           ? tags.length === 1
          //             ? "1 tag"
          //             : tags.length + " tags"
          //           : ""}
          //       </span>
          //     );
          //   }
          // },
          {
            title: "Unit Count",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "101px"
            },
            headerStyle: {
              width: "101px"
            },
            searchable: false,
            customSort: (a, b) => +a.stop.quantity - +b.stop.quantity,
            render: rowData => {
              return <span>{rowData.stop.quantity}</span>;
            }
          },
          {
            title: "Unit Weight",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "106px"
            },
            headerStyle: {
              width: "106px"
            },
            searchable: false,
            customSort: (a, b) => +a.stop.weight - +b.stop.weight,
            render: rowData => {
              return <span>{rowData.stop.weight}</span>;
            }
          },
          {
            title: "Unit Volume",
            field: "date",
            editable: "never",

            cellStyle: {
              width: "110px"
            },
            headerStyle: {
              width: "110px"
            },
            searchable: false,
            customSort: (a, b) => +a.stop.volume - +b.stop.volume,
            render: rowData => {
              return <span>{rowData.stop.volume}</span>;
            }
          }
          // TODO: do not need to show this column yet
          // {
          //   title: "Planned ETA",
          //   field: "date",
          //   editable: "never",

          //   cellStyle: {
          //     width: "113px"
          //   },
          //   headerStyle: {
          //     width: "113px"
          //   },
          //   searchable: false,
          //   customSort: (a, b) =>
          //     a.stop.original_arr_time - b.stop.original_arr_time,
          //   render: rowData => {
          //     return (
          //       <span>{secondsToTime(rowData.stop.original_arr_time)}</span>
          //     );
          //   }
          // },
          // TODO: do not need to show this column yet
          // {
          //   title: "Tracking Number",
          //   field: "date",
          //   editable: "never",

          //   cellStyle: {
          //     width: "149px"
          //   },
          //   headerStyle: {
          //     width: "149px"
          //   },
          //   searchable: false,
          //   render: rowData => {
          //     return <span>{rowData.stop.tracking.id}</span>;
          //   }
          // }
        ]}
      />
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, loadingCircle, selected, app, appName]);

  return (
    <>
      {commonMaterialTable}
      {currentAction === "add" && (
        <ProjectsEditDialog
          isApp
          open={currentAction === "add"}
          currentAction={currentAction}
          saveDisabled={saveDisabled}
          setSaveDisabled={setSaveDisabled}
          setCurrentAction={setCurrentAction}
        />
      )}
    </>
  );
}

const mapStateToProps = state => ({
  appStops: state.account.appStops
});

export default connect(mapStateToProps, { setAppStops, getAppProjects })(
  SingleApp
);
